import { useState } from "react";
import { resetPassword } from "../../api/auth";
import { showError } from "../../utils/toast";

export default function PasswordResetRequest({ onBack, onClose }) {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!email.includes("@")) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await resetPassword(email);
      setIsSubmitted(true);
      // Success UI shown below - no need for toast
    } catch (err) {
      // Django always returns success to prevent email enumeration
      // But we'll handle errors just in case
      if (err.response?.data) {
        const apiErrors = err.response.data;
        if (apiErrors.email) {
          setErrors({
            email: Array.isArray(apiErrors.email)
              ? apiErrors.email[0]
              : apiErrors.email,
          });
        } else {
          showError(err.userMessage || "Failed to send reset email");
        }
      } else {
        showError("Failed to send reset email. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="p-6 text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-positive bg-opacity-10 
          rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-positive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 
                002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-primary-content">
          Check your email
        </h3>
        <p className="text-secondary-content">
          We've sent password reset instructions to{" "}
          <span className="font-medium text-primary-content">{email}</span>
        </p>
        <p className="text-sm text-secondary-content">
          Didn't receive the email? Check your spam folder or try again.
        </p>
        <div className="flex gap-3 pt-2">
          <button
            onClick={onBack}
            className="flex-1 px-4 py-2 border border-divider 
              text-primary-content rounded-md hover:bg-hover
              transition-colors"
          >
            Back to Login
          </button>
          <button
            onClick={() => {
              setEmail("");
              setIsSubmitted(false);
              setErrors({});
            }}
            className="flex-1 px-4 py-2 bg-primary text-white 
              rounded-md hover:bg-primary-hover transition-colors"
          >
            Send Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-primary-content mb-2">
          Reset your password
        </h3>
        <p className="text-sm text-secondary-content">
          Enter your email address and we'll send you instructions to reset
          your password.
        </p>
      </div>

      <div>
        <label
          htmlFor="reset-email"
          className="block text-sm font-medium text-primary-content mb-1"
        >
          Email Address
        </label>
        <input
          type="email"
          id="reset-email"
          name="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors({});
          }}
          className={`w-full px-3 py-2 bg-page border rounded-md 
            text-primary-content placeholder-secondary-content
            focus:outline-none focus:ring-2 focus:ring-primary 
            ${errors.email ? "border-negative" : "border-divider"}`}
          placeholder="Enter your email"
          autoComplete="email"
          autoFocus
        />
        {errors.email && (
          <p className="mt-1 text-sm text-negative">{errors.email}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-primary text-white py-2 px-4 
          rounded-md hover:bg-primary-hover focus:outline-none 
          focus:ring-2 focus:ring-primary focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors font-medium"
      >
        {isLoading ? "Sending..." : "Send Reset Link"}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-sm text-secondary-content 
          hover:text-primary-content transition-colors"
      >
        ← Back to Login
      </button>
    </form>
  );
}
